/**
 * The map editor page (B6, FD §3.3) inside PlanFrame: tool bar, the map, and the side panel with the Selected
 * card, Unplaced tray, Legend and (Advanced) plan settings and screenshot controls. PlanFrame's top bar gets
 * Undo, Redo, Download picture and the Simple/Advanced switch (EditorToolbar).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PlanDoc, PlanId } from '../../../shared/types';
import { pictureFileName, renderPlanCanvas, renderPlanPng, renderThumbPng, loadImage, saveBlob, PICTURE_PX, THUMB_PX } from '../../render/export';
import { ICONS, PLACEABLE_ICONS } from '../../render/palette';
import * as api from '../../api';
import { EditorStore, loadSpecies, registerEditor, useEditor, useRegisteredEditor, type ToolId } from '../../store/editor';
import type { MapController } from '../../tools/controller';
import { deleteObject, makeRoom } from '../../tools/edits';
import { PlanFrame, type PlanContext } from '../shell/PlanFrame';
import { errorText, toast } from '../shell/dialogs';
import { SpeciesSelect } from './fields';
import { MapCanvas } from './MapCanvas';
import { Legend } from './panels/Legend';
import { OverlayPanel } from './panels/OverlayPanel';
import { PlanSettings } from './panels/PlanSettings';
import { SelectedCard } from './panels/SelectedCard';
import { UnplacedTray } from './panels/UnplacedTray';
import './editor.css';

/** The editor route: PlanFrame with the editor inside. */
export function EditorPage({ planId }: { planId: PlanId }) {
  return (
    <PlanFrame
      planId={planId}
      toolbar={(ctx) => <EditorToolbar ctx={ctx} />}
      getThumbnail={thumbnail}
      renderPreview={(doc) => <PlanPicture doc={doc} />}
    >
      {(ctx) => <Editor ctx={ctx} />}
    </PlanFrame>
  );
}

/** The 320 px card picture for My plans. */
async function thumbnail(doc: PlanDoc): Promise<Blob | null> {
  const species = await loadSpecies().catch(() => null);
  return renderThumbPng(doc, species);
}

/** A small picture of a plan (History previews). */
export function PlanPicture({ doc }: { doc: PlanDoc }) {
  const [species, setSpecies] = useState<Awaited<ReturnType<typeof loadSpecies>> | null>(null);
  useEffect(() => { loadSpecies().then(setSpecies, () => undefined); }, []);
  const url = useMemo(() => renderPlanCanvas(doc, THUMB_PX, { species, labels: false, scaleBar: false }).toDataURL('image/png'), [doc, species]);
  return <img src={url} alt="Picture of this version of the plan" style={{ width: '100%', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }} />;
}

interface ToolDef { id: ToolId; label: string; title: string; advanced?: boolean }
const TOOLS: ToolDef[] = [
  { id: 'select', label: '👆 Select', title: 'Select, move and reshape' },
  { id: 'road', label: '🛤️ Path', title: 'Draw a path: click points, double-click to finish' },
  { id: 'habitat', label: '🦁 Habitat', title: 'Pick an animal, then draw its habitat' },
  { id: 'lake', label: '💧 Lake', title: 'Draw a lake' },
  { id: 'river', label: '〰️ River', title: 'Draw a river' },
  { id: 'icon', label: '🏪 Shop', title: 'Place a shop or service' },
  { id: 'delete', label: '🗑️ Delete', title: 'Click something to remove it' },
  { id: 'cut', label: '✂️ Cut path', title: 'Click a path to cut it in two', advanced: true },
  { id: 'join', label: '🔗 Join paths', title: 'Join two paths into one, or a path end onto another path', advanced: true },
];

/** The editor below PlanFrame's top bar. */
export function Editor({ ctx }: { ctx: PlanContext }) {
  // PlanFrame may hand over a new context (a fresh autosave after a StrictMode re-run); always commit to the latest
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const [store] = useState(() => new EditorStore({ planId: ctx.planId, doc: ctx.getDoc(), onCommit: (doc) => ctxRef.current.commit(doc) }));
  const controllerRef = useRef<MapController | null>(null);

  useEffect(() => {
    registerEditor(ctx.planId, store);
    // this browser may still hold a tray from before the tray lived in the plan: bring it in once
    const adopted = store.adoptOldTray();
    if (adopted) store.say(`${adopted} unplaced habitat${adopted === 1 ? '' : 's'} kept on this computer ${adopted === 1 ? 'was' : 'were'} added to the plan's Unplaced list.`);
    return () => {
      registerEditor(ctx.planId, null);
      store.dispose();
    };
  }, [ctx.planId, store]);

  // A rename in the top bar changes the doc's name; keep the editor's doc in line without an undo step.
  useEffect(() => {
    if (store.getState().doc.name !== ctx.meta.name) {
      const name = ctx.meta.name;
      store.quietly((d) => { d.name = name; });
    }
  }, [ctx.meta.name, store]);

  useKeyboard(store, controllerRef);

  const advanced = useEditor(store, (s) => s.advanced);
  // a new selection brings the Selected card into view
  const selection = useEditor(store, (s) => s.selection);
  const sideRef = useRef<HTMLElement>(null);
  useEffect(() => { if (selection) sideRef.current?.scrollTo({ top: 0 }); }, [selection]);
  return (
    <div className="editor">
      <ToolBar store={store} />
      <ToolOptions store={store} />
      <div className="editor-body">
        <MapCanvas store={store} controllerRef={controllerRef} />
        <aside className="editor-side" aria-label="Plan details" ref={sideRef}>
          <SelectedCard store={store} />
          <UnplacedTray store={store} />
          <Legend store={store} />
          {advanced && <PlanSettings store={store} />}
          {advanced && <OverlayPanel store={store} />}
        </aside>
      </div>
    </div>
  );
}

function ToolBar({ store }: { store: EditorStore }) {
  const tool = useEditor(store, (s) => s.tool);
  const advanced = useEditor(store, (s) => s.advanced);
  const follow = useEditor(store, (s) => s.follow);
  const grid = useEditor(store, (s) => s.showGrid);
  return (
    <div className="editor-tools" role="toolbar" aria-label="Tools">
      {TOOLS.filter((t) => !t.advanced || advanced).map((t) => (
        <button key={t.id} type="button" className={tool === t.id ? 'on' : ''} aria-pressed={tool === t.id} title={t.title} onClick={() => store.setTool(t.id)}>
          {t.label}
        </button>
      ))}
      <span className="sep" />
      <label className="check" title="When you move or widen a path, nearby habitats, pools and shops move with it, and a new path trims the habitats it crosses">
        <input type="checkbox" checked={follow} onChange={(e) => store.setFollow(e.target.checked)} /> Habitats follow paths
      </label>
      <label className="check"><input type="checkbox" checked={grid} onChange={(e) => store.setShowGrid(e.target.checked)} /> Grid</label>
    </div>
  );
}

function ToolOptions({ store }: { store: EditorStore }) {
  const tool = useEditor(store, (s) => s.tool);
  const species = useEditor(store, (s) => s.species);
  const toolSpecies = useEditor(store, (s) => s.toolSpecies);
  const toolIcon = useEditor(store, (s) => s.toolIcon);
  let body: ReactNode = null;
  if (tool === 'habitat') {
    const sp = species?.find((s) => s.id === toolSpecies);
    body = species ? (
      <>
        <span>Animal:</span>
        <SpeciesSelect species={species} value={toolSpecies} label="Animal for the new habitat" onChange={(id) => store.setToolSpecies(id)} />
        {sp && <span className="muted">then click around its outline on the map, or</span>}
        {sp && <button type="button" onClick={() => { makeRoom(store, sp.id, sp.groupSize.typical); store.setTool('select'); }}>Make room for it</button>}
      </>
    ) : <span className="muted">Loading the animals…</span>;
  } else if (tool === 'icon') {
    body = (
      <>
        <span>Place:</span>
        {PLACEABLE_ICONS.map((t) => (
          <button key={t} type="button" className={toolIcon === t ? 'on' : ''} aria-pressed={toolIcon === t} onClick={() => store.setToolIcon(t)}>
            {ICONS[t].icon} {ICONS[t].label}
          </button>
        ))}
      </>
    );
  }
  if (!body) return null;
  return <div className="tool-options editor-tools">{body}</div>;
}

/** Keyboard shortcuts while the editor is open. */
function useKeyboard(store: EditorStore, ctl: React.RefObject<MapController | null>) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName) || t.isContentEditable)) return;
      if (document.querySelector('.backdrop')) return; // a dialog is open
      const c = ctl.current, mod = e.ctrlKey || e.metaKey, key = e.key.toLowerCase();
      if (mod && key === 'z' && !e.shiftKey) { e.preventDefault(); store.undo(); return; }
      if (mod && (key === 'y' || (key === 'z' && e.shiftKey))) { e.preventDefault(); store.redo(); return; }
      if (mod || e.altKey) return;
      if (e.key === 'Escape') {
        if (c?.isDrawing()) c.resetTool();
        else if (store.getState().tool !== 'select') store.setTool('select');
        else store.select(null);
      } else if (e.key === 'Enter') {
        if (c?.isDrawing()) { e.preventDefault(); c.finish(); }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (c?.isDrawing()) { e.preventDefault(); c.undoPoint(); return; }
        const sel = store.getState().selection;
        if (sel && sel.kind !== 'plot') { e.preventDefault(); deleteObject(store, sel); }
      } else if (e.key === '+' || e.key === '=') c?.zoomBy(1.4);
      else if (e.key === '-' || e.key === '_') c?.zoomBy(1 / 1.4);
      else if (e.key === '0') store.fit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store, ctl]);
}

/** Undo, Redo, Download picture and the Simple/Advanced switch, shown in PlanFrame's top bar. */
export function EditorToolbar({ ctx }: { ctx: PlanContext }) {
  const store = useRegisteredEditor(ctx.planId);
  const canUndo = useEditor(store, (s) => s.canUndo);
  const canRedo = useEditor(store, (s) => s.canRedo);
  const undoLabel = useEditor(store, (s) => s.undoLabel);
  const redoLabel = useEditor(store, (s) => s.redoLabel);
  const advanced = useEditor(store, (s) => s.advanced);
  const [busy, setBusy] = useState(false);
  if (!store) return null;
  const download = async () => {
    setBusy(true);
    try {
      const s = store.getState();
      const overlay = s.doc.overlay ? await loadImage(api.overlayUrl(ctx.planId, s.doc.overlay.file)) : null;
      const blob = await renderPlanPng(s.doc, PICTURE_PX, { species: s.species, overlayImage: overlay, grid: s.showGrid });
      if (!blob) throw new Error("The picture couldn't be made in this browser.");
      saveBlob(blob, pictureFileName(ctx.meta.name));
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" disabled={!canUndo} onClick={() => store.undo()} title={canUndo ? `Undo: ${undoLabel} (Ctrl+Z)` : 'Nothing to undo'}>↶ Undo</button>
      <button type="button" disabled={!canRedo} onClick={() => store.redo()} title={canRedo ? `Redo: ${redoLabel} (Ctrl+Y)` : 'Nothing to redo'}>↷ Redo</button>
      <span className="grow" />
      <button type="button" disabled={busy} onClick={() => void download()} title="Save a picture of the map, to share or keep on a second screen">
        {busy ? 'Making picture…' : '🖼️ Download picture'}
      </button>
      <label className="check" title="Advanced adds width grips, cut and join, lock, re-arrange, the boundary, animal data and the screenshot">
        <input type="checkbox" checked={!!advanced} onChange={(e) => store.setAdvanced(e.target.checked)} /> Advanced
      </label>
    </>
  );
}
